/*
 * IconButton (Figma 119:14922) / ActionButton (119:15081). "Use Secondary for table row
 * actions and Ghost for toolbar actions."
 * | Variant           | Size   | Padding  | Radius | Fill                                                    |
 * | ghost (toolbar)   | 32×32  | 4px 8px  | 6px    | none; hover --interaction-hover; pressed --interaction-active |
 * | secondary         | 32×32  | 4px 8px  | 6px    | --interaction-selected + --shadow-icon-button-secondary |
 * | secondary + kbd   | 64×32  | 4px 8px  | 6px    | as secondary; icon and Kbd hint, space-between; hint opacity 0 until the row is hovered/focused |
 * Icon 16px. Focus: --focus-m.
 * Touch screens (not in Figma): --size-touch-target square (40×40) on a coarse pointer, and no
 * key hint where nothing hovers (hover: none): there is no keyboard to press it on, so the
 * button is the icon alone, always visible.
 */
import type { ButtonHTMLAttributes } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { Kbd } from './Kbd'

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    icon: string
    /** Accessible name (required: the button has no visible text). */
    label: string
    variant?: 'ghost' | 'secondary'
    /** Keyboard shortcut hint shown inside the button (row actions). */
    kbd?: string
    pressed?: boolean
}

export function IconButton({ icon, label, variant = 'ghost', kbd, pressed, className, type = 'button', ...rest }: IconButtonProps) {
    return (
        <button
            type={type}
            aria-label={label}
            title={label}
            aria-pressed={pressed}
            className={cx(
                'press group/iconbutton relative inline-flex h-[32px] shrink-0 cursor-pointer items-center overflow-clip rounded-[6px] px-[8px] py-[4px]',
                kbd ? 'w-[64px] justify-between hover-none:aspect-square hover-none:w-auto hover-none:justify-center' : 'w-[32px] justify-center gap-[6px]',
                'pointer-coarse:size-[var(--size-touch-target)]',
                variant === 'secondary'
                    ? 'bg-[var(--interaction-selected)] shadow-[var(--shadow-icon-button-secondary)]'
                    : cx('hover:bg-[var(--interaction-hover)] active:bg-[var(--interaction-active)]', pressed && 'bg-[var(--interaction-active)]'),
                'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                className,
            )}
            {...rest}
        >
            <Icon src={icon} size={16} />
            {kbd && (
                <Kbd className="opacity-0 transition-opacity group-focus-visible/iconbutton:opacity-100 hover-none:hidden [[data-row-active]_&]:opacity-100">
                    {kbd}
                </Kbd>
            )}
        </button>
    )
}
